import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, InjectionToken, NgModule } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MatDialogModule, MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTreeModule } from '@angular/material/tree';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { MatListModule } from '@angular/material/list';
import { MatGridListModule } from '@angular/material/grid-list';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatTableModule } from '@angular/material/table';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';

import { BsDropdownModule } from 'ngx-bootstrap/dropdown';

import { CookieService } from 'ngx-cookie-service';
import { NgxWidgetGridModule } from 'ngx-widget-grid';
import { NgxFileDropModule } from 'ngx-file-drop';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './components/header/header/header.component';
import { EHealthDialogComponent } from './dialogs/e-health-dialog/e-health-dialog.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PatientDialogComponent } from './dialogs/patient-dialog/patient-dialog.component';
import { LogoinfoComponent } from './components/header/logoinfo/logoinfo.component';
import { PatientsearchComponent } from './components/header/patientsearch/patientsearch.component';
import { PatientinfoComponent } from './components/header/patientinfo/patientinfo.component';
import { PatientEHealthInfoComponent } from './components/header/patientehealthinfo/patientehealthinfo.component';
import { AuthenticateComponent } from './dialogs/authenticate/authenticate.component';
import { BecareService } from './services/becare.service';
import { AppInitService } from './services/app-init.service';
import { TypeManagerService } from './services/type-manager.service';
import { PhpSessionInterceptor } from './phpsession.interceptor';
import { UserinfoComponent } from './components/header/userinfo/userinfo.component';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fal } from '@fortawesome/pro-light-svg-icons';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { far } from '@fortawesome/pro-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { LegacyContentComponent } from './components/content/legacy-content/legacy-content.component';
import { PatientRemindersLoginDialogComponent } from './dialogs/patient-reminders-login-dialog/patient-reminders-login-dialog.component';
import { PatientNoteComponent } from './components/patient/patient-note/patient-note.component';
import { PatientIdentityComponent } from './components/patient/patient-identity/patient-identity.component';
import { PatientMedicalComponent } from './components/patient/patient-medical/patient-medical.component';
import { PatientInsuranceComponent } from './components/patient/patient-insurance/patient-insurance.component';
import { PatientAdministrativeComponent } from './components/patient/patient-administrative/patient-administrative.component';
import { DoctorSelectionComponent } from './components/doctor/doctor-selection/doctor-selection.component';
import { InsuranceSelectionComponent } from './components/insurance/insurance-selection/insurance-selection.component';
import { PatientAddressComponent } from './components/patient/patient-address/patient-address.component';
import { PatientComponent } from './components/patient/patient/patient.component';
import { MenuLeftComponent } from './components/menu/menu-left/menu-left.component';
import { AngularFileUploaderModule } from './angular-file-uploader/angular-file-uploader.module';
import { AuthComponent } from './components/auth/auth/auth.component';
import { ContentComponent } from './components/content/content/content.component';
import { EHealthMessageDialogComponent } from './dialogs/e-health/e-health-message-dialog/e-health-message-dialog.component';
import { EHealthMessageViewerDialogComponent } from './dialogs/e-health/e-health-messageviewer-dialog/e-health-messageviewer-dialog.component';
import { ImportConfirmDialogComponent } from './dialogs/e-health/import-confirm-dialog/import-confirm-dialog.component';
import { SendConfirmDialogComponent } from './dialogs/e-health/send-confirm-dialog/send-confirm-dialog.component';
import { DeleteConfirmDialogComponent } from './dialogs/e-health/delete-confirm-dialog/delete-confirm-dialog.component';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/moment';
import * as moment from 'moment';
import { AgendaAdminAllDialogComponent } from './dialogs/admin/agenda-admin-all-dialog/agenda-admin-all-dialog.component';
import { ManageType } from 'src/app/decorators/type-manager-decorator';
import { AgendaComponent } from './components/agenda/agenda/agenda.component';
import { ReportTableComponent } from './components/report/report-table/report-table.component';
import { SNItemTableModule } from './snitem-table/snitem-table.module';
import { ReportRowComponent } from './components/report/report-row/report-row.component';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { ReportRowIconsComponent } from './components/report/report-row-icons/report-row-icons.component';
import { ContactTableComponent } from './components/contact/contact-table/contact-table.component';
import { ContactRowComponent } from './components/contact/contact-row/contact-row.component';
import { ContactRowIconsComponent } from './components/contact/contact-row-icons/contact-row-icons.component';
import { ContactService } from './services/contact.service';
import { ReportService } from './services/report.service';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { CrToBrPipe } from './pipes/cr-to-br.pipe';
import { ActionIconsComponent } from './components/header/action-icons/action-icons.component';
import { PrescriptionEntryComponent } from './components/medication/prescription/prescription-entry/prescription-entry.component';
import { PrescriptionTableComponent } from './components/medication/prescription/prescription-table/prescription-table.component';
import { PrescriptionComponent } from './components/medication/prescription/prescription/prescription.component';
import { PrescriptionDialogComponent } from './dialogs/prescription/prescription-dialog/prescription-dialog.component';
import { DrugEntryComponent } from './components/medication/drug/drug-entry/drug-entry.component';
import { DrugTableComponent } from './components/medication/drug/drug-table/drug-table.component';
import { DrugComponent } from './components/medication/drug/drug/drug.component';
import { PrescriptionEditComponent } from './components/medication/prescription/prescription-edit/prescription-edit.component';
import { PrescriptionEditDialogComponent } from './dialogs/prescription/prescription-edit-dialog/prescription-edit-dialog.component';
import { PrescriptionPosologyComponent } from './components/medication/prescription/prescription-posology/prescription-posology.component';
import { DrugDialogComponent } from './dialogs/drug/drug-dialog/drug-dialog.component';
import { AgendaListComponent } from './components/agenda/agenda-list/agenda-list.component';
import { UserDepartmentsAdminDialogComponent } from './dialogs/admin/user-departments-admin-dialog/user-departments-admin-dialog.component';
import { UserDepartmentListComponent } from './components/user/user-department-list/user-department-list.component';
import { DepartmentComponent } from './components/user/department/department.component';
import { UserListComponent } from './components/user/user-list/user-list.component';
import { UserListRowComponent } from './components/user/user-list-row/user-list-row.component';
import { UserSelectionComponent } from './components/user/user-selection/user-selection.component';
import { AgendaEventDialogComponent } from './components/agenda/agenda-event-dialog/agenda-event-dialog.component';
import { VerifyCloseComponent } from './components/agenda/verify-close/verify-close.component';

import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { ItemAccessTableComponent } from './components/item/item-access-table/item-access-table.component';
import { ItemAccessDialogComponent } from './dialogs/item/item-access-dialog/item-access-dialog.component';
import { ContactServicesRowComponent } from './components/contact/contact-services-row/contact-services-row.component';
import { HtmlEntitiesPipe } from './pipes/html-entities.pipe';
import { PatientotherinfoComponent } from './components/header/patientotherinfo/patientotherinfo.component';
import { AddressbookComponent } from './components/header/addressbook/addressbook.component';
import { DashboardComponent } from './components/dashboard/dashboard/dashboard.component';
import { RecordPanelComponent } from './components/record/record-panel/record-panel.component';
import { ContactWidgetComponent } from './components/contact/contact-widget/contact-widget.component';
import { ContactsPanelComponent } from './components/contact/contacts-panel/contacts-panel.component';
import { PrescriptionOutputDialogComponent } from './dialogs/prescription/prescription-output-dialog/prescription-output-dialog.component';
import { MenuRightComponent } from './components/menu/menu-right/menu-right.component';
import { MedicationWidgetComponent } from './components/medication/medication-widget/medication-widget.component';
import { MedicationTableComponent } from './components/medication/medication-table/medication-table.component';
import { MedicationListComponent } from './components/medication/medication-list/medication-list.component';
import { MedicationRowComponent } from './components/medication/medication-row/medication-row.component';
import { ProblemListComponent } from './components/problem/problem-list/problem-list.component';
import { ProblemRowComponent } from './components/problem/problem-row/problem-row.component';
import { EHealthDmgDialogComponent } from './dialogs/e-health/e-health-dmg-dialog/e-health-dmg-dialog.component';
import { ProblemWidgetComponent } from './components/problem/problem-widget/problem-widget.component';
import { MedicationPanelComponent } from './components/medication/medication-panel/medication-panel.component';
import { ProblemTableComponent } from './components/problem/problem-table/problem-table.component';
import { ProblemPanelComponent } from './components/problem/problem-panel/problem-panel.component';
import { ProtocolRowComponent } from './components/protocol/protocol-row/protocol-row.component';
import { ProtocolTableComponent } from './components/protocol/protocol-table/protocol-table.component';
import { ProtocolPanelComponent } from './components/protocol/protocol-panel/protocol-panel.component';
import { LaboTableComponent } from './components/labo/labo-table/labo-table.component';
import { LaboRowComponent } from './components/labo/labo-row/labo-row.component';
import { LaboPanelComponent } from './components/labo/labo-panel/labo-panel.component';
import { RecordTreeComponent } from './components/record/record-tree/record-tree.component';
import { UserAdminDialogComponent } from './dialogs/admin/user-admin-dialog/user-admin-dialog.component';
import { UserGroupListComponent } from './components/user/user-group-list/user-group-list.component';
import { UserDomainListComponent } from './components/user/user-domain-list/user-domain-list.component';
import { PatientCareTeamDialogComponent } from './dialogs/patient/patient-care-team-dialog/patient-care-team-dialog.component';
import { ProtocolWidgetComponent } from './components/protocol/protocol-widget/protocol-widget.component';
import { LaboWidgetComponent } from './components/labo/labo-widget/labo-widget.component';
import { ReportWidgetComponent } from './components/report/report-widget/report-widget.component';
import { ContactServicesPanelComponent } from './components/service/contact-services-panel/contact-services-panel.component';
import { ServiceCodeGroupComponent } from './components/service/service-code-group/service-code-group.component';
import { ReportPanelComponent } from './components/report/report-panel/report-panel.component';
import { PatientEidDialogComponent } from './dialogs/patient/patient-eid-dialog/patient-eid-dialog.component';
import { ValidateDialogComponent } from './dialogs/validate-dialog/validate-dialog.component';
import { WaitingDialogComponent } from './dialogs/waiting-dialog/waiting-dialog.component';
import { DrugSearchComponent } from './components/medication/drug/drug-search/drug-search.component';
import { ContactRowPrescriptionComponent } from './components/contact/contact-row-prescription/contact-row-prescription.component';
import { AgendaHeaderListComponent } from './components/agenda/agenda-header-list/agenda-header-list.component';
import { UserProfileDirective } from './directives/user-profile.directive';
import { AgendaWaitingHeaderComponent } from './components/agenda/agenda-waiting-header/agenda-waiting-header.component';
import { ContactContentSimpleComponent } from './components/contact/contents/contact-content-simple/contact-content-simple.component';
import { ContactEditDialogComponent } from './dialogs/contact/contact-edit-dialog/contact-edit-dialog.component';
import { RecordContainerComponent } from './components/record/record-container/record-container.component';
import { ContactBiometricsSimpleComponent } from './components/contact/contents/contact-biometrics-simple/contact-biometrics-simple.component';
import { UserOpenItemsComponent } from './components/header/user-open-items/user-open-items.component';
import { ContactComponent } from './components/contact/contact/contact.component';
import { ContactPanelComponent } from './components/contact/contact-panel/contact-panel.component';
import { ContactContentRxComponent } from './components/contact/contents/contact-content-rx/contact-content-rx.component';
import { ContactContentKineComponent } from './components/contact/contents/contact-content-kine/contact-content-kine.component';
import { ContactContentLogoComponent } from './components/contact/contents/contact-content-logo/contact-content-logo.component';
import { ContactTypeListComponent } from './components/contact/contact-type-list/contact-type-list.component';
import { EnumToArrayPipe } from './pipes/enum-to-array.pipe';
import { ContactEditorComponent } from './components/contact/contact-editor/contact-editor.component';
import { ContactLogoTreatmentComponent } from './components/contact/contents/contact-logo-treatment/contact-logo-treatment.component';
import { ContactKineTreatmentComponent } from './components/contact/contents/contact-kine-treatment/contact-kine-treatment.component';
import { ContactContentOphtalmoComponent } from './components/contact/contents/contact-content-ophtalmo/contact-content-ophtalmo.component';
import { ContactOphtalmoTreatmentComponent } from './components/contact/contents/contact-ophtalmo-treatment/contact-ophtalmo-treatment.component';
import { PrescriptionPanelComponent } from './components/medication/prescription/prescription-panel/prescription-panel.component';
import { TreatmentDialogComponent } from './dialogs/treatment/treatment-dialog/treatment-dialog.component';
import { TreatmentKineComponent } from './components/treatment/treatment-kine/treatment-kine.component';
import { TreatmentLogoComponent } from './components/treatment/treatment-logo/treatment-logo.component';
import { ServiceCodeComponent } from './components/service/service-code/service-code.component';
import { SubstringPipe } from './pipes/substring.pipe';
import { ContactServicesComponent } from './components/contact/contact-services/contact-services.component';
import { ContactTypesGroupListComponent } from './components/contact/contact-types-group-list/contact-types-group-list.component';
import { ContactDocumentsComponent } from './components/contact/contents/contact-documents/contact-documents.component';
import { DocumentTemplateListComponent } from './components/document/document-template-list/document-template-list.component';
import { LaboDialogComponent } from './dialogs/labo/labo-dialog/labo-dialog.component';
import { CardInfoComponent } from './components/header/card-info/card-info.component';
import { PrescriptionSideComponent } from './components/medication/prescription/prescription-side/prescription-side.component';
import { ProtocolDialogComponent } from './dialogs/protocol/protocol-dialog/protocol-dialog.component';
import { SmsTableComponent } from './components/sms/sms-table/sms-table.component';
import { SmsRowComponent } from './components/sms/sms-row/sms-row.component';
import { SmsConversationDialogComponent } from './dialogs/sms/sms-conversation-dialog/sms-conversation-dialog.component';
import { ContactServicesTableComponent } from './components/contact/contact-services-table/contact-services-table.component';
import { ContactUserServicesPanelComponent } from './components/contact/contact-user-services-panel/contact-user-services-panel.component';
import { ContactUserServicesDialogComponent } from './dialogs/contact/contact-user-services-dialog/contact-user-services-dialog.component';
import { PrintersComponent } from './components/print/printers/printers.component';
import { PrintItemDialogComponent } from './dialogs/print/print-item-dialog/print-item-dialog.component';
import { JobstatusComponent } from './components/header/jobstatus/jobstatus.component';
import { EHealthDmgListDialogComponent } from './dialogs/e-health/e-health-dmg-list-dialog/e-health-dmg-list-dialog.component';
import { AdminServiceCodeComponent } from './components/admin/service/admin-service-code/admin-service-code.component';
import { AdminServiceCodeManagementDialogComponent } from './dialogs/admin/admin-service-code-management-dialog/admin-service-code-management-dialog.component';
import { AdminServiceCodeValueComponent } from './components/admin/service/admin-service-code-value/admin-service-code-value.component';
import { ContactDocumentsRowComponent } from './components/contact/contact-documents-row/contact-documents-row.component';
import { ContactContentCardioComponent } from './components/contact/contents/contact-content-cardio/contact-content-cardio.component';
import { ContactCardioTreatmentComponent } from './components/contact/contents/contact-cardio-treatment/contact-cardio-treatment.component';
import { AdminVaccinationManagementDialogComponent } from './dialogs/admin/admin-vaccination-management-dialog/admin-vaccination-management-dialog.component';
import { DiseaseListComponent } from './components/vaccination/disease-list/disease-list.component';
import { VaccineListComponent } from './components/vaccination/vaccine-list/vaccine-list.component';
import { VaccineComponent } from './components/vaccination/vaccine/vaccine.component';
import { VaccineDiseasesComponent } from './components/vaccination/vaccine-diseases/vaccine-diseases.component';
import { VaccineSchemasComponent } from './components/vaccination/vaccine-schemas/vaccine-schemas.component';
import { VaccineSchemaComponent } from './components/vaccination/vaccine-schema/vaccine-schema.component';
import { OneFieldDialogComponent } from './dialogs/onefield-dialog/onefield-dialog.component';
import { EHealthInsurabilityDialogComponent } from './dialogs/e-health/e-health-assurability-dialog/e-health-assurability-dialog.component';
import { ContactServicesDialogComponent } from './dialogs/contact/contact-services-dialog/contact-services-dialog.component';
import { AgendaAppointmentTableComponent } from './components/agenda/agenda-appointment-table/agenda-appointment-table.component';
import { AgendaAppointmentRowComponent } from './components/agenda/agenda-appointment-row/agenda-appointment-row.component';
import { UserAccessListComponent } from './components/user/user-access-list/user-access-list.component';
import { AdminReportValidationDialogComponent } from './dialogs/admin/admin-report-validation-dialog/admin-report-validation-dialog.component';
import { ContactRowContentComponent } from './components/contact/contact-row-content/contact-row-content.component';
import { ContactRxSerieComponent } from './components/contact/contents/contact-rx-serie/contact-rx-serie.component';
import { RxSerieDialogComponent } from './dialogs/contact/rx-serie-dialog/rx-serie-dialog.component';
import { RxSerie2DialogComponent } from './dialogs/contact/rx-serie-2-dialog/rx-serie-2-dialog.component';
import { RxSerie2Component } from './components/contact/rx-serie-2/rx-serie-2.component';
import { RxImageTableComponent } from './components/imaging/rx-image-table/rx-image-table.component';
import { RxImageViewerComponent } from './components/imaging/rx-image-viewer/rx-image-viewer.component';
import { ReportDialogComponent } from './dialogs/report/report-dialog/report-dialog.component';
import { ProtocolEditDialogComponent } from './dialogs/protocol/protocol-edit-dialog/protocol-edit-dialog.component';
import { EHealthTransactionDialogComponent } from './dialogs/e-health/e-health-transaction-dialog/e-health-transaction-dialog.component';

export function momentAdapterFactory() {
  return adapterFactory(moment);
}

export const ROWITEM = new InjectionToken<string[]>('rowitem');

export function appInit(appInitService: AppInitService) {
  return () => appInitService.load();
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    EHealthDialogComponent,
    PatientDialogComponent,
    LogoinfoComponent,
    PatientsearchComponent,
    PatientinfoComponent,
    PatientEHealthInfoComponent,
    AuthenticateComponent,
    UserinfoComponent,
    LegacyContentComponent,
    PatientRemindersLoginDialogComponent,
    PatientNoteComponent,
    PatientIdentityComponent,
    PatientMedicalComponent,
    PatientInsuranceComponent,
    PatientAdministrativeComponent,
    DoctorSelectionComponent,
    InsuranceSelectionComponent,
    PatientAddressComponent,
    PatientComponent,
    MenuLeftComponent,
    AuthComponent,
    ContentComponent,
    EHealthMessageDialogComponent,
    EHealthMessageViewerDialogComponent,
    ImportConfirmDialogComponent,
    SendConfirmDialogComponent,
    DeleteConfirmDialogComponent,
    AgendaAdminAllDialogComponent,
    AgendaComponent,
    ReportTableComponent,
    ReportRowComponent,
    ReportRowIconsComponent,
    ContactTableComponent,
    ContactRowComponent,
    ContactRowIconsComponent,
    SafeHtmlPipe,
    CrToBrPipe,
    ActionIconsComponent,
    PrescriptionEntryComponent,
    PrescriptionTableComponent,
    PrescriptionComponent,
    PrescriptionDialogComponent,
    DrugEntryComponent,
    DrugTableComponent,
    DrugComponent,
    PrescriptionEditComponent,
    PrescriptionEditDialogComponent,
    PrescriptionPosologyComponent,
    DrugDialogComponent,
    AgendaListComponent,
    UserDepartmentsAdminDialogComponent,
    UserDepartmentListComponent,
    DepartmentComponent,
    UserListComponent,
    UserListRowComponent,
    UserSelectionComponent,
    AgendaEventDialogComponent,
    VerifyCloseComponent,
    ItemAccessTableComponent,
    ItemAccessDialogComponent,
    ContactServicesRowComponent,
    HtmlEntitiesPipe,
    PatientotherinfoComponent,
    AddressbookComponent,
    DashboardComponent,
    RecordPanelComponent,
    ContactWidgetComponent,
    ContactsPanelComponent,
    PrescriptionOutputDialogComponent,
    MenuRightComponent,
    MedicationWidgetComponent,
    MedicationTableComponent,
    MedicationListComponent,
    MedicationRowComponent,
    ProblemListComponent,
    ProblemRowComponent,
    EHealthDmgDialogComponent,
    ProblemWidgetComponent,
    MedicationPanelComponent,
    ProblemTableComponent,
    ProblemPanelComponent,
    ProtocolRowComponent,
    ProtocolTableComponent,
    ProtocolPanelComponent,
    LaboTableComponent,
    LaboRowComponent,
    LaboPanelComponent,
    RecordTreeComponent,
    UserAdminDialogComponent,
    UserGroupListComponent,
    UserDomainListComponent,
    PatientCareTeamDialogComponent,
    ProtocolWidgetComponent,
    LaboWidgetComponent,
    ReportWidgetComponent,
    ServiceCodeGroupComponent,
    ContactServicesPanelComponent,
    ReportPanelComponent,
    PatientEidDialogComponent,
    ValidateDialogComponent,
    WaitingDialogComponent,
    DrugSearchComponent,
    ContactRowPrescriptionComponent,
    AgendaHeaderListComponent,
    UserProfileDirective,
    AgendaWaitingHeaderComponent,
    ContactContentSimpleComponent,
    ContactEditDialogComponent,
    RecordContainerComponent,
    ContactBiometricsSimpleComponent,
    UserOpenItemsComponent,
    ContactComponent,
    ContactPanelComponent,
    ContactContentRxComponent,
    ContactContentKineComponent,
    ContactContentLogoComponent,
    ContactTypeListComponent,
    EnumToArrayPipe,
    ContactEditorComponent,
    ContactLogoTreatmentComponent,
    ContactKineTreatmentComponent,
    ContactContentOphtalmoComponent,
    ContactOphtalmoTreatmentComponent,
    PrescriptionPanelComponent,
    TreatmentDialogComponent,
    TreatmentKineComponent,
    TreatmentLogoComponent,
    ServiceCodeComponent,
    SubstringPipe,
    ContactServicesComponent,
    ContactTypesGroupListComponent,
    ContactDocumentsComponent,
    DocumentTemplateListComponent,
    LaboDialogComponent,
    CardInfoComponent,
    PrescriptionSideComponent,
    ProtocolDialogComponent,
    SmsTableComponent,
    SmsRowComponent,
    SmsConversationDialogComponent,
    ContactServicesTableComponent,
    ContactUserServicesPanelComponent,
    ContactUserServicesDialogComponent,
    PrintersComponent,
    PrintItemDialogComponent,
    JobstatusComponent,
    EHealthDmgListDialogComponent,
    AdminServiceCodeComponent,
    AdminServiceCodeManagementDialogComponent,
    AdminServiceCodeValueComponent,
    ContactDocumentsRowComponent,
    ContactContentCardioComponent,
    ContactCardioTreatmentComponent,
    AdminVaccinationManagementDialogComponent,
    DiseaseListComponent,
    VaccineListComponent,
    VaccineComponent,
    VaccineDiseasesComponent,
    VaccineSchemasComponent,
    VaccineSchemaComponent,
    OneFieldDialogComponent,
    EHealthInsurabilityDialogComponent,
    ContactServicesDialogComponent,
    AgendaAppointmentTableComponent,
    AgendaAppointmentRowComponent,
    UserAccessListComponent,
    AdminReportValidationDialogComponent,
    ContactRowContentComponent,
    ContactRxSerieComponent,
    RxSerieDialogComponent,
    RxSerie2DialogComponent,
    RxSerie2Component,
    RxImageTableComponent,
    RxImageViewerComponent,
    ReportDialogComponent,
    ProtocolEditDialogComponent,
    EHealthTransactionDialogComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    MatDialogModule,
    MatSidenavModule,
    MatCardModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule,
    MatTabsModule,
    MatTreeModule,
    MatIconModule,
    DragDropModule,
    MatRippleModule,
    MatListModule,
    MatGridListModule,
    FormsModule, ReactiveFormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    FlexLayoutModule,
    MatSnackBarModule,
    MatToolbarModule,
    MatMenuModule,
    FontAwesomeModule,
    AngularFileUploaderModule,
    SNItemTableModule,
    CKEditorModule,
    NgxWidgetGridModule,
    MatExpansionModule,
    MatProgressBarModule,
    NgxFileDropModule,
    MatRadioModule,
    BsDropdownModule.forRoot(),
    CalendarModule.forRoot({ provide: DateAdapter, useFactory: momentAdapterFactory }),
  ],
  providers: [
    CookieService,
    BecareService,
    TypeManagerService,
    {
        provide : HTTP_INTERCEPTORS,
        useClass: PhpSessionInterceptor,
        multi   : true,
    },
    { provide: ROWITEM, useValue: '' },
    AppInitService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInit,
      multi: true,
      deps: [AppInitService]
    },
    {provide: MAT_DATE_LOCALE, useValue: 'fr-BE'}
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(private falibrary: FaIconLibrary, private contact: ContactService, private report: ReportService) {
    falibrary.addIconPacks(fal, fas, far, fab);
  }
}
